import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal";
import { useDialogStyles } from "../../theme/redesign";
import { DialogClose, FilterChips, KpiTile, NegocioPager, PageFrame, RecordsState, SearchField, tNegocio, usePager } from "./shared";
import { useStyles as useNegocioStyles } from "./styles";
import { useRecords, bumpNegocio } from "./Lists";
import { AccountEditDialog, AccountPeek, ProductMark } from "./AccountPeek";
import { accountLiveStatus, statusColor } from "./helpers";
import { productTitle } from "./catalog";

const t = (key, vars) => {
  let text = tNegocio(`pages.emails.${key}`);
  if (vars) Object.entries(vars).forEach(([k, v]) => { text = text.split(`{${k}}`).join(String(v)); });
  return text;
};
const norm = (v) => String(v || "").trim().toLowerCase();
const dmy = (iso) => {
  const m = String(iso || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};
/** Orden de estados en cada correo: lo que pide atención primero. */
const LIVE_ORDER = { fault: 0, expiring: 1, available: 2, sold: 3, idle: 4, expired: 5 };

const useStyles = makeStyles((theme) => ({
  row: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 1.3fr) minmax(260px, 2.2fr) auto",
    gap: 14,
    alignItems: "center",
    padding: "12px 14px",
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr", gap: 10 },
  },
  email: { fontWeight: 800, wordBreak: "break-all", display: "flex", alignItems: "center", gap: 4 },
  caption: { color: theme.palette.text.secondary, fontSize: 12.5 },
  accounts: { display: "flex", flexWrap: "wrap", gap: 6 },
  accountChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px 4px 4px",
    borderRadius: 999,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    cursor: "pointer",
    font: "inherit",
    color: theme.palette.text.primary,
    maxWidth: "100%",
    "&:hover": { borderColor: theme.palette.primary.main, boxShadow: "0 2px 8px rgba(15,23,42,0.08)" },
  },
  chipText: { display: "flex", flexDirection: "column", lineHeight: 1.15, minWidth: 0, textAlign: "left" },
  chipName: { fontWeight: 700, fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  chipMeta: { fontSize: 11.5, fontWeight: 700 },
  actions: { display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end", flexWrap: "wrap", [theme.breakpoints.down("sm")]: { justifyContent: "flex-start" } },
  password: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12.5, color: theme.palette.text.secondary },
  empty: { fontSize: 12.5, color: theme.palette.text.secondary, fontStyle: "italic" },
}));

function EmailDialog({ initial, onClose, onSaved }) {
  const dlg = useDialogStyles();
  const editing = !!initial?.id;
  const [email, setEmail] = useState(initial?.email || "");
  const [password, setPassword] = useState("");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = { email, password, notes };
      if (editing) await api.put(`/negocio/emails/${initial.id}`, body);
      else await api.post("/negocio/emails", body);
      toast.success(editing ? t("saved") : t("created"));
      bumpNegocio("emails");
      onSaved();
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth classes={{ paper: dlg.paper }}>
      <DialogClose onClose={onClose} disabled={busy} />
      <DialogTitle>{editing ? t("editTitle") : t("newTitle")}</DialogTitle>
      <DialogContent dividers>
        <TextField fullWidth margin="dense" variant="outlined" type="email" label={t("email")} value={email} autoFocus={!editing} onChange={(e) => setEmail(e.target.value)} />
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          type={show ? "text" : "password"}
          label={t("password")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          helperText={editing && initial?.hasPassword ? t("passwordKeep") : t("passwordHint")}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShow((v) => !v)}>{show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField fullWidth margin="dense" variant="outlined" multiline rows={3} label={t("notes")} value={notes} onChange={(e) => setNotes(e.target.value)} helperText={t("notesHint")} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{tNegocio("cancel")}</Button>
        <Button variant="contained" color="primary" onClick={save} disabled={busy || !email.trim()}>{tNegocio("save")}</Button>
      </DialogActions>
    </Dialog>
  );
}

function AccountChip({ account, onOpen }) {
  const classes = useStyles();
  const live = accountLiveStatus(account);
  const color = statusColor(live);
  const end = dmy(account.endDate);
  return (
    <Tooltip title={`${t("openAccount")}${account.code ? ` · ${account.code}` : ""}`}>
      <button type="button" className={classes.accountChip} style={{ borderColor: live === "fault" ? color : undefined }} onClick={() => onOpen(account)}>
        <ProductMark product={account.product} size={24} circle link={false} />
        <span className={classes.chipText}>
          <span className={classes.chipName}>{productTitle(account.product) || t("noProduct")}</span>
          <span className={classes.chipMeta} style={{ color }}>
            {tNegocio(`status.${live}`)}{end ? ` · ${end}` : ""}
          </span>
        </span>
      </button>
    </Tooltip>
  );
}

function EmailRow({ row, index, onOpenAccount, onCreateAccount, onEdit, onRegister, onDelete }) {
  const classes = useStyles();
  const negocio = useNegocioStyles();
  const [showPass, setShowPass] = useState(false);
  const worst = row.accounts.length ? row.accounts[0].live : null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(row.email);
      toast.success(t("copied"));
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <div className={negocio.serviceCustomerCard} style={{ animationDelay: `${Math.min(index, 5) * 35}ms`, borderLeftColor: worst ? statusColor(worst) : "#94a3b8" }}>
      <div className={classes.row}>
        <div style={{ minWidth: 0 }}>
          <div className={classes.email}>
            <MailOutlineIcon fontSize="small" color="action" />
            <span>{row.email}</span>
            <IconButton size="small" onClick={copy} aria-label={t("copy")}><FileCopyIcon style={{ fontSize: 15 }} /></IconButton>
          </div>
          <div className={classes.caption}>
            {row.accounts.length ? t("accountsCount", { n: row.accounts.length }) : t("noAccounts")}
            {row.registered ? ` · ${t("registered")}` : ""}
          </div>
          {row.registered?.hasPassword ? (
            <span className={classes.password}>
              <VpnKeyIcon style={{ fontSize: 13 }} />
              {showPass ? row.registered.password : "••••••"}
              <IconButton size="small" onClick={() => setShowPass((v) => !v)}>{showPass ? <VisibilityOff style={{ fontSize: 14 }} /> : <Visibility style={{ fontSize: 14 }} />}</IconButton>
            </span>
          ) : null}
          {row.registered?.notes ? <div className={classes.caption} style={{ whiteSpace: "pre-wrap" }}>{row.registered.notes}</div> : null}
        </div>
        <div className={classes.accounts}>
          {row.accounts.length ? (
            row.accounts.map((a) => <AccountChip key={a.id} account={a} onOpen={onOpenAccount} />)
          ) : (
            <span className={classes.empty}>{t("noAccountsHint")}</span>
          )}
        </div>
        <div className={classes.actions}>
          <Button size="small" variant="outlined" color="primary" startIcon={<AddIcon />} onClick={() => onCreateAccount(row.email)}>
            {t("createAccount")}
          </Button>
          {row.registered ? (
            <>
              <Tooltip title={t("edit")}><IconButton size="small" onClick={() => onEdit(row.registered)}><EditIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title={t("delete")}><IconButton size="small" onClick={() => onDelete(row.registered)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
            </>
          ) : (
            <Tooltip title={t("registerHint")}><Button size="small" onClick={() => onRegister(row.email)}>{t("register")}</Button></Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

export default function EmailsPage() {
  const negocio = useNegocioStyles();
  const accountsList = useRecords("accounts");
  const emailsList = useRecords("emails");
  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");
  const [dialog, setDialog] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  // La cuenta se abre aquí mismo con el visor de Cuentas (ver, editar, renovar, perfiles) sin salir de Correos.
  const [peek, setPeek] = useState(null);
  // «Crear cuenta» también se queda en Correos: formulario de cuenta nueva con el correo ya puesto.
  const [creating, setCreating] = useState(null);

  const rows = useMemo(() => {
    const byEmail = new Map();
    const ensure = (email) => {
      const key = norm(email);
      if (!key) return null;
      if (!byEmail.has(key)) byEmail.set(key, { email: key, accounts: [], registered: null });
      return byEmail.get(key);
    };
    (emailsList.records || []).forEach((e) => {
      const row = ensure(e.email);
      if (row) row.registered = e;
    });
    (accountsList.records || []).forEach((a) => {
      const row = ensure(a.email);
      if (row) row.accounts.push({ ...a, live: accountLiveStatus(a) });
    });
    return [...byEmail.values()].map((row) => {
      row.accounts.sort((a, b) => (LIVE_ORDER[a.live] ?? 9) - (LIVE_ORDER[b.live] ?? 9) || productTitle(a.product).localeCompare(productTitle(b.product)));
      return row;
    });
  }, [accountsList.records, emailsList.records]);

  const counts = useMemo(() => ({
    all: rows.length,
    withAccounts: rows.filter((r) => r.accounts.length).length,
    free: rows.filter((r) => !r.accounts.length).length,
    fault: rows.filter((r) => r.accounts.some((a) => a.live === "fault")).length,
    active: rows.filter((r) => r.accounts.some((a) => ["available", "sold", "expiring"].includes(a.live))).length,
    expired: rows.filter((r) => r.accounts.length && r.accounts.every((a) => a.live === "expired")).length,
  }), [rows]);

  const visible = useMemo(() => {
    const q = norm(query);
    return rows
      .filter((r) => {
        if (filter === "with") return r.accounts.length > 0;
        if (filter === "free") return !r.accounts.length;
        if (filter === "fault") return r.accounts.some((a) => a.live === "fault");
        if (filter === "active") return r.accounts.some((a) => ["available", "sold", "expiring"].includes(a.live));
        if (filter === "expired") return r.accounts.length && r.accounts.every((a) => a.live === "expired");
        return true;
      })
      .filter((r) => !q || r.email.includes(q) || r.accounts.some((a) => norm(productTitle(a.product)).includes(q) || norm(a.code).includes(q)) || norm(r.registered?.notes).includes(q))
      .sort((a, b) => a.email.localeCompare(b.email));
  }, [rows, filter, query]);
  const pager = usePager(visible, 25);

  const openAccount = (account) => setPeek(account);
  const reloadAll = () => Promise.all([accountsList.reload({ soft: true }), emailsList.reload({ soft: true })]);
  const createAccount = (email) => setCreating({ email });
  const removeRegistered = async () => {
    const row = toDelete;
    setToDelete(null);
    try {
      await api.delete(`/negocio/emails/${row.id}`);
      toast.success(t("deleted"));
      bumpNegocio("emails");
      emailsList.reload({ soft: true });
    } catch (error) {
      toastError(error);
    }
  };

  const loading = accountsList.loading || emailsList.loading;
  return (
    <PageFrame
      title={t("title")}
      subtitle={t("subtitle")}
      actions={
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setDialog({})}>
          {t("new")}
        </Button>
      }
    >
      <div className={negocio.kpiGrid}>
        <KpiTile icon={MailOutlineIcon} label={t("kpi.total")} value={loading ? "…" : counts.all} onClick={() => setFilter("")} />
        <KpiTile icon={CheckCircleIcon} tone="kpiToneGood" label={t("kpi.active")} value={loading ? "…" : counts.active} onClick={() => setFilter("active")} />
        <KpiTile icon={ReportProblemIcon} tone="kpiToneBad" label={t("kpi.fault")} value={loading ? "…" : counts.fault} onClick={() => setFilter("fault")} />
        <KpiTile icon={LinkOffIcon} tone="kpiToneWarn" label={t("kpi.free")} value={loading ? "…" : counts.free} onClick={() => setFilter("free")} />
      </div>
      <div className={negocio.filterBar}>
        <FilterChips
          value={filter}
          onChange={setFilter}
          options={[
            { value: "", label: `${tNegocio("filters.all")} (${counts.all})` },
            { value: "active", label: `${t("filter.active")} (${counts.active})` },
            { value: "fault", label: `${t("filter.fault")} (${counts.fault})` },
            { value: "expired", label: `${t("filter.expired")} (${counts.expired})` },
            { value: "with", label: `${t("filter.with")} (${counts.withAccounts})` },
            { value: "free", label: `${t("filter.free")} (${counts.free})` },
          ]}
        />
      </div>
      <SearchField fullWidth value={query} onChange={setQuery} placeholder={t("search")} />
      <RecordsState loading={loading} count={visible.length} emptyTitle={rows.length ? t("emptyFiltered") : t("empty")} emptySubtitle={rows.length ? undefined : t("emptyHint")}>
        <div className={`${negocio.tableWrap} ${negocio.tableWrapFill}`}>
          {pager.slice.map((row, index) => (
            <EmailRow
              key={row.email}
              row={row}
              index={index}
              onOpenAccount={openAccount}
              onCreateAccount={createAccount}
              onEdit={(registered) => setDialog(registered)}
              onRegister={(email) => setDialog({ email })}
              onDelete={(registered) => setToDelete(registered)}
            />
          ))}
        </div>
        <NegocioPager count={pager.count} page={pager.page} rowsPerPage={pager.rowsPerPage} onChangePage={pager.setPage} onChangeRowsPerPage={pager.setRowsPerPage} hideBelow={25} />
      </RecordsState>
      {dialog ? (
        <EmailDialog
          initial={dialog}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            emailsList.reload({ soft: true });
          }}
        />
      ) : null}
      {creating ? (
        <AccountEditDialog
          open
          account={creating}
          onClose={() => setCreating(null)}
          onSaved={() => reloadAll()}
        />
      ) : null}
      <AccountPeek
        open={!!peek}
        account={peek}
        onClose={() => setPeek(null)}
        onChanged={reloadAll}
      />
      <ConfirmationModal title={t("deleteTitle")} open={!!toDelete} onClose={() => setToDelete(null)} onConfirm={removeRegistered}>
        {t("deleteBody", { email: toDelete?.email || "" })}
      </ConfirmationModal>
    </PageFrame>
  );
}
